import { IcebergIcon } from '@tradesolution/iceberg-ui-react';
import { CompareStatus, qaCompareSimpleValue } from 'pages/QA/components/QACardDetails/utils';
import { useEffect, useState, useMemo, useRef } from 'react';
import { KjedePath } from './KjedePath';
import { Button, Collapse, Form, Row, Col, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import CompareIcon from '../../../CompareIcon';
import Style from '../../../../index.module.css';
import GrupperingPicker from 'components/GrupperingPicker';
import { IEnhetsgruppeNode } from 'components/GrupperingPicker/types';
import { Enhetsgruppe } from 'services/KjederegisteretAdminApi/ChangeRequestApi/types';
import RegionalkjedePicker from 'components/RegionalkjedePicker';
import MarkedsKjedePicker from 'components/MarkedsKjedePicker';
import { PickedKjede, PickerMode } from 'components/KjedePicker';
import { KjedeHistorikk } from 'services/KjederegisteretAdminApi/EnhetApi/types';
import { MembershipSuggestions } from 'services/QAApi/types';
import { KjedeType } from 'services/KjederegisteretAdminApi/KjedeApi/types';
import type {
  InnkjopInnmeldingsInfo,
  InnkjopUtmeldingsInfo,
  KjedeInnmeldingsInfo,
  KjedeUtmeldingsInfo,
} from '../..';
import { NorwegianDatepicker } from 'components/NorwegianDatepicker';
import DateHelper from 'utils/DateHelper';
import { format } from 'date-fns';
import { EnhetsgrupperingPath } from './EnhetsgrupperingPath';

// Helper component for displaying membership information with overflow handling
interface MembershipDisplayProps {
  activeMarkedskjede?: KjedeHistorikk | null;
  aktiveEnhetsgrupperinger: Enhetsgruppe[];
}

const MembershipDisplay: React.FC<MembershipDisplayProps> = ({
  activeMarkedskjede,
  aktiveEnhetsgrupperinger,
}) => {
  const items: string[] = [];

  // Add markedskjede if exists
  if (activeMarkedskjede?.nameHierarchy) {
    const kjedeName = Array.isArray(activeMarkedskjede.nameHierarchy)
      ? activeMarkedskjede.nameHierarchy[activeMarkedskjede.nameHierarchy.length - 1]
      : activeMarkedskjede.nameHierarchy;
    const dateStr = activeMarkedskjede.fraOgMedDato
      ? ` (${format(activeMarkedskjede.fraOgMedDato, 'dd.MM.yyyy')})`
      : '';
    items.push(`${kjedeName}${dateStr}`);
  }

  // Add enhetsgrupper with dates
  aktiveEnhetsgrupperinger?.forEach((innkjop) => {
    if (innkjop?.toppnivaaEnhetsgrupperingnavn) {
      const dateStr = innkjop.innmeldingsdato
        ? ` (${format(innkjop.innmeldingsdato, 'dd.MM.yyyy')})`
        : '';
      items.push(`${innkjop.toppnivaaEnhetsgrupperingnavn}${dateStr}`);
    }
  });

  if (items.length === 0) return null;

  const fullText = items.join(', ');

  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{fullText}</Tooltip>}>
      <span
        style={{
          display: 'block',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
        }}
      >
        {fullText}
      </span>
    </OverlayTrigger>
  );
};

interface KjedeInfo {
  kjedeNr?: number | null;
  innmeldingsdato?: Date | null;
  utmeldingsdato?: Date | null;
  medlemsnummer?: string | null;
}

interface EnhetsgruppeInfo {
  enhetsgrupperingNr: number;
  innmeldingsdato?: Date | null;
  utmeldingsdato?: Date | null;
  medlemsnummer?: string;
}

export interface Props {
  onKjedeInnmelding: (innmeldingsInfo: KjedeInnmeldingsInfo) => void;
  onKjedeUtmelding: (utmeldingsInfo: KjedeUtmeldingsInfo) => void;
  onInnkjopInnmelding: (innmeldingsInfo: InnkjopInnmeldingsInfo) => void;
  onInnkjopUtmelding: (utmeldingsInfo: InnkjopUtmeldingsInfo) => void;
  onChange: (field: string, value: string | Date | number | null | undefined) => void;
  changeRequest: {
    id: string;
    medlemHos?: string;
    medlemsNummer?: string;
    medlemFraOgMedDato?: Date | null;
    medlemTilOgMedDato?: Date | null;
    medlemHosEkstraInfo?: MembershipSuggestions | null;
  };
  error?: string;
  selectedEnhetsgruppering: EnhetsgruppeInfo;
  onEnhetsgrupperingChange: (selectedEnhetsgrupperingNr: string | null) => void;
  enhetsgrupperingError?: string;
  aktiveEnhetsgrupperinger: Enhetsgruppe[];
  selectedRegionalkjede?: KjedeInfo;
  regionalkjedeError?: string;
  activeRegionalkjede?: KjedeHistorikk | null;
  futureRegionalkjede: KjedeHistorikk | null;
  selectedMarkedskjede?: KjedeInfo;
  markedskjedeError?: string;
  activeMarkedskjede?: KjedeHistorikk | null;
  futureMarkedsKjede: KjedeHistorikk | null;
  minFraOgMedDato?: Date | null;
}

export interface ViewData {
  showRegionalkjedeUtmeldingsDato: boolean;
  showRegionalInnmeldingsFields: boolean;
  showMarkedskjedeUtmeldingsDato: boolean;
  showMarkedskjedeInnmeldingsFields: boolean;
  showInnkjøpInnmeldingsFields?: boolean;
  showInnkjøpUtmeldingsDato: boolean;
  showOtherEnhetsgrupperinger: boolean;
}

export const getViewData = (props: Props): ViewData => ({
  showRegionalInnmeldingsFields: !!props.selectedRegionalkjede,
  showRegionalkjedeUtmeldingsDato: !!props.activeRegionalkjede,
  showMarkedskjedeInnmeldingsFields: !!props.selectedMarkedskjede,
  showMarkedskjedeUtmeldingsDato: !!props.activeMarkedskjede,
  showInnkjøpInnmeldingsFields: !!props.selectedEnhetsgruppering?.enhetsgrupperingNr,
  showInnkjøpUtmeldingsDato: props.aktiveEnhetsgrupperinger.some(
    (eg) =>
      eg.enhetsgrupperingsNr === props.changeRequest.medlemHosEkstraInfo?.innkjopssamarbeid?.nummer,
  ),
  showOtherEnhetsgrupperinger:
    props.aktiveEnhetsgrupperinger.filter(
      (o) =>
        props.changeRequest.medlemHosEkstraInfo?.innkjopssamarbeid?.nummer !==
        o.enhetsgrupperingsNr,
    ).length > 0,
});

const MedlemhosRow = (props: Props) => {
  const [isExpandRow, setIsExpandRow] = useState<boolean>(true);
  const handleExpandRowClick = () => setIsExpandRow((prev) => !prev);

  const transferKjede = (kjedeType: KjedeType) => {
    if (kjedeType === KjedeType.Marked) {
      if (props.changeRequest.medlemHosEkstraInfo?.markedsKjede) {
        if (props.changeRequest.medlemTilOgMedDato) {
          props.onKjedeUtmelding({
            kjedeNr: props.changeRequest.medlemHosEkstraInfo.markedsKjede.nummer,
            utmeldingsdato: DateHelper.addNorwegianTimezone(props.changeRequest.medlemTilOgMedDato),
            kjedeType: KjedeType.Marked,
          });
        } else {
          props.onKjedeInnmelding({
            kjedeNr: props.changeRequest.medlemHosEkstraInfo.markedsKjede.nummer,
            innmeldingsdato: DateHelper.addNorwegianTimezone(
              props.changeRequest.medlemFraOgMedDato ?? DateHelper.getFirstDateOfMonth(),
            ),
            medlemsnummer: props.changeRequest.medlemsNummer,
            kjedeType: KjedeType.Marked,
          });
        }
      }
    } else if (kjedeType === KjedeType.Region) {
      if (props.changeRequest.medlemHosEkstraInfo?.regionalKjede) {
        if (props.changeRequest.medlemTilOgMedDato) {
          props.onKjedeUtmelding({
            kjedeNr: props.changeRequest.medlemHosEkstraInfo.regionalKjede.nummer,
            utmeldingsdato: DateHelper.addNorwegianTimezone(props.changeRequest.medlemTilOgMedDato),
            kjedeType: KjedeType.Region,
          });
        } else {
          props.onKjedeInnmelding({
            kjedeNr: props.changeRequest.medlemHosEkstraInfo.regionalKjede.nummer,
            innmeldingsdato: DateHelper.addNorwegianTimezone(
              props.changeRequest.medlemFraOgMedDato ?? DateHelper.getFirstDateOfMonth(),
            ),
            medlemsnummer: props.changeRequest.medlemsNummer,
            kjedeType: KjedeType.Region,
          });
        }
      }
    }
  };

  const transferInnkjop = () => {
    if (props.changeRequest.medlemHosEkstraInfo?.innkjopssamarbeid) {
      if (props.changeRequest.medlemTilOgMedDato) {
        props.onInnkjopUtmelding({
          enhetsgrupperingNr: Number(
            props.changeRequest.medlemHosEkstraInfo.innkjopssamarbeid.nummer,
          ),
          utmeldingsdato: DateHelper.addNorwegianTimezone(props.changeRequest.medlemTilOgMedDato),
        });
      } else {
        props.onInnkjopInnmelding({
          enhetsgrupperingNr: Number(
            props.changeRequest.medlemHosEkstraInfo.innkjopssamarbeid.nummer,
          ),
          innmeldingsdato: DateHelper.addNorwegianTimezone(
            props.changeRequest.medlemFraOgMedDato ?? DateHelper.getFirstDateOfMonth(),
          ),
          medlemsnummer: props.changeRequest.medlemsNummer,
        });
      }
    }
  };

  const [markedskjedeCompareStatus, setMarkedskjedeCompareStatus] = useState<CompareStatus>('ulik');
  const [regionalkjedeCompareStatus, setRegionalkjedeCompareStatus] =
    useState<CompareStatus>('ulik');
  const [innkjopCompareStatus, setInnkjopCompareStatus] = useState<CompareStatus>('ulik');
  const hasAutoSelectedEnhetsgruppering = useRef(false);

  const headerCompareStatus: CompareStatus = useMemo(() => {
    const statuses = [markedskjedeCompareStatus, regionalkjedeCompareStatus, innkjopCompareStatus];
    if (statuses.some((s) => s === 'ulik')) return 'ulik';
    if (statuses.some((s) => s === 'ny')) return 'ny';
    // Treat 'ignorer' the same as 'lik' for the aggregate icon
    return 'lik';
  }, [markedskjedeCompareStatus, regionalkjedeCompareStatus, innkjopCompareStatus]);

  useEffect(() => {
    setMarkedskjedeCompareStatus(
      props.activeMarkedskjede
        ? 'ignorer'
        : qaCompareSimpleValue(
            props.changeRequest?.medlemHosEkstraInfo?.markedsKjede?.nummer ?? '',
            props.selectedMarkedskjede?.kjedeNr?.toString() ?? '',
          ),
    );
  }, [
    props.selectedMarkedskjede,
    props.changeRequest?.medlemHosEkstraInfo?.markedsKjede?.nummer,
    props.activeMarkedskjede,
  ]);

  useEffect(() => {
    setRegionalkjedeCompareStatus(
      props.activeRegionalkjede
        ? 'ignorer'
        : qaCompareSimpleValue(
            props.changeRequest?.medlemHosEkstraInfo?.regionalKjede?.nummer ?? '',
            props.selectedRegionalkjede?.kjedeNr?.toString() ?? '',
          ),
    );
  }, [
    props.selectedRegionalkjede,
    props.changeRequest?.medlemHosEkstraInfo?.regionalKjede?.nummer,
    props.activeRegionalkjede,
  ]);

  useEffect(() => {
    const suggestedInnkjop = props.changeRequest?.medlemHosEkstraInfo?.innkjopssamarbeid;

    if (!suggestedInnkjop?.nummer) {
      setInnkjopCompareStatus('ignorer');
      return;
    }

    // Check exact match by nummer
    const matchByNummer = props.aktiveEnhetsgrupperinger.some(
      (o) => o.enhetsgrupperingsNr === suggestedInnkjop.nummer,
    );

    // Check match by toppnivå name (e.g., EVAT under SYSTEMKJØP AS matches SYSTEMKJØP AS)
    const matchByToppnivaaName = props.aktiveEnhetsgrupperinger.some(
      (o) =>
        o.toppnivaaEnhetsgrupperingnavn?.toLowerCase() === suggestedInnkjop.navn?.toLowerCase(),
    );

    // Check if manually selected matches
    const matchBySelection =
      props.selectedEnhetsgruppering?.enhetsgrupperingNr?.toString() === suggestedInnkjop.nummer;

    if (matchByNummer || matchByToppnivaaName || matchBySelection) {
      setInnkjopCompareStatus('lik');
    } else {
      setInnkjopCompareStatus('ny');
    }
  }, [
    props.selectedEnhetsgruppering,
    props.changeRequest?.medlemHosEkstraInfo?.innkjopssamarbeid,
    props.aktiveEnhetsgrupperinger,
  ]);

  // Auto-select enhetsgruppering when toppnivå matches suggested innkjøpssamarbeid
  useEffect(() => {
    const suggestedInnkjop = props.changeRequest?.medlemHosEkstraInfo?.innkjopssamarbeid;

    // Only auto-select once, if there's a suggestion and nothing is selected yet
    if (
      hasAutoSelectedEnhetsgruppering.current ||
      !suggestedInnkjop?.navn ||
      props.selectedEnhetsgruppering?.enhetsgrupperingNr
    ) {
      return;
    }

    // Find a gruppering where toppnivå matches the suggested name
    const matchingGruppering = props.aktiveEnhetsgrupperinger.find(
      (o) =>
        o.toppnivaaEnhetsgrupperingnavn?.toLowerCase() === suggestedInnkjop.navn?.toLowerCase(),
    );

    if (matchingGruppering?.enhetsgrupperingsNr) {
      hasAutoSelectedEnhetsgruppering.current = true;
      props.onEnhetsgrupperingChange(matchingGruppering.enhetsgrupperingsNr);
    }
  }, [
    props.changeRequest?.medlemHosEkstraInfo?.innkjopssamarbeid,
    props.aktiveEnhetsgrupperinger,
    props.selectedEnhetsgruppering?.enhetsgrupperingNr,
  ]);

  const handlePickedGruppering = (grupperinger: IEnhetsgruppeNode[]) => {
    if (grupperinger && grupperinger.length > 0) {
      const grupperingNr = grupperinger[0].nr;
      props.onEnhetsgrupperingChange(grupperingNr.toString());
      props.onChange('enhetsgrupperingsMedlemsnummer', props.changeRequest.medlemsNummer || '');
      props.onChange(
        'enhetsgrupperingsInnmeldingsdato',
        props.changeRequest.medlemFraOgMedDato || null,
      );
    } else {
      props.onEnhetsgrupperingChange(null);
    }
  };

  const handlePickedRegionalkjede = (kjeder: PickedKjede[]) => {
    if (kjeder.length > 0) {
      const selectedKjede = kjeder[0];
      props.onKjedeInnmelding({
        kjedeNr: selectedKjede.kjedenr,
        innmeldingsdato: props.selectedMarkedskjede?.innmeldingsdato ?? null,
        medlemsnummer: props.selectedMarkedskjede?.medlemsnummer ?? null,
        kjedeType: KjedeType.Region,
      });
    } else {
      props.onKjedeInnmelding({
        kjedeNr: null,
        innmeldingsdato: null,
        medlemsnummer: null,
        kjedeType: KjedeType.Region,
      });
    }
  };

  const handlePickedMarkedskjede = (kjeder: PickedKjede[]) => {
    if (kjeder.length > 0) {
      const selectedKjede = kjeder[0];
      props.onKjedeInnmelding({
        kjedeNr: selectedKjede.kjedenr,
        innmeldingsdato: props.selectedMarkedskjede?.innmeldingsdato ?? null,
        medlemsnummer: props.selectedMarkedskjede?.medlemsnummer ?? null,
        kjedeType: KjedeType.Marked,
      });
    } else {
      props.onKjedeInnmelding({
        kjedeNr: null,
        innmeldingsdato: null,
        medlemsnummer: null,
        kjedeType: KjedeType.Marked,
      });
    }
  };

  const [viewData, setViewData] = useState<ViewData>(getViewData(props));

  useEffect(() => {
    setViewData(getViewData(props));
  }, [props.activeRegionalkjede, props.activeMarkedskjede, props.selectedEnhetsgruppering]);

  useEffect(() => {
    const hasMarkedskjedeChanges =
      markedskjedeCompareStatus === 'ulik' || markedskjedeCompareStatus === 'ny';
    const hasRegionalkjedeChanges =
      regionalkjedeCompareStatus === 'ulik' || regionalkjedeCompareStatus === 'ny';
    const hasInnkjopChanges = innkjopCompareStatus === 'ulik' || innkjopCompareStatus === 'ny';

    const hasMembershipData = !!(
      props.changeRequest.medlemHos ||
      props.changeRequest.medlemHosEkstraInfo?.markedsKjede ||
      props.changeRequest.medlemHosEkstraInfo?.regionalKjede ||
      props.changeRequest.medlemHosEkstraInfo?.innkjopssamarbeid
    );

    if (
      (hasMarkedskjedeChanges || hasRegionalkjedeChanges || hasInnkjopChanges) &&
      hasMembershipData
    ) {
      setIsExpandRow(true);
    }
  }, [
    markedskjedeCompareStatus,
    regionalkjedeCompareStatus,
    innkjopCompareStatus,
    props.changeRequest.medlemHos,
    props.changeRequest.medlemHosEkstraInfo,
  ]);

  useEffect(() => {
    setIsExpandRow(false);
  }, [props.changeRequest.id]);

  const isActiveMemberOfSuggestedInnkjop = !!(
    props.changeRequest.medlemHosEkstraInfo?.innkjopssamarbeid &&
    props.aktiveEnhetsgrupperinger.some(
      (e) =>
        e.enhetsgrupperingsNr === props.changeRequest.medlemHosEkstraInfo?.innkjopssamarbeid.nummer,
    )
  );

  // Check if the selected enhetsgruppering (from picker) is one the entity is already a member of
  const isActiveMemberOfSelectedInnkjop = !!(
    props.selectedEnhetsgruppering?.enhetsgrupperingNr &&
    props.aktiveEnhetsgrupperinger.some(
      (e) =>
        e.enhetsgrupperingsNr?.toString() ===
        props.selectedEnhetsgruppering.enhetsgrupperingNr?.toString(),
    )
  );

  const otherActiveEnhetsgrupperinger = props.aktiveEnhetsgrupperinger.filter(
    (o) =>
      props.changeRequest.medlemHosEkstraInfo?.innkjopssamarbeid?.nummer !== o.enhetsgrupperingsNr,
  );

  const [showAllActiveEnhetsgrupper, setShowAllActiveEnhetsgrupper] = useState(false);

  // Utmelding: når dato settes skal vi bruke onInnkjopUtmelding slik som kjede-utmeldinger,
  // fordi parent form bygger en kopi (copy.enhetsgrupperingsUtmeldingsdato = utmeldingsdato).
  // Når dato fjernes nullstiller vi kun feltet via onChange.
  const settMeldUt = (dato: Date | null | undefined) => {
    const nr = Number(props.changeRequest.medlemHosEkstraInfo?.innkjopssamarbeid.nummer);

    if (dato && nr) {
      props.onInnkjopUtmelding({
        enhetsgrupperingNr: nr,
        utmeldingsdato: DateHelper.addNorwegianTimezone
          ? DateHelper.addNorwegianTimezone(dato)
          : dato,
      });
    } else {
      props.onChange('enhetsgrupperingsUtmeldingsdato', null);
      if (nr) props.onChange('enhetsgrupperingsNr', nr);
    }
  };

  return (
    <>
      <div className={Style.changeRequestRowHeaderContainer} onClick={handleExpandRowClick}>
        <h6 className={Style.changeRequestRowHeader}>
          <Row className="g-0">
            <Col xs={5} className="d-flex align-items-center gap-1">
              {!isExpandRow && <CompareIcon compareStatus={headerCompareStatus} size="sm" />}
              <span className={Style.changeRequestRowHeaderText}>
                Medlem hos {props.changeRequest.medlemHos}
              </span>
            </Col>
            <Col xs={2}></Col>
            <Col xs={4} className="d-flex align-items-center" style={{ minWidth: 0 }}>
              <div className="w-100" style={{ minWidth: 0 }}>
                <MembershipDisplay
                  activeMarkedskjede={props.activeMarkedskjede}
                  aktiveEnhetsgrupperinger={props.aktiveEnhetsgrupperinger}
                />
              </div>
            </Col>
            <Col xs={1} className="d-flex align-items-center justify-content-end">
              <Button variant="link" size="sm">
                <IcebergIcon icon={isExpandRow ? 'chevron-up' : 'chevron-down'} bold />
              </Button>
            </Col>
          </Row>
        </h6>
      </div>
      <Collapse in={isExpandRow}>
        <div>
          {/* ------------------------------------------------------------------------------Venstresiden-------------------------------------------------------------  */}
          {/* Markedskjede */}
          <Row className={Style.formGroupRow}>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <Form.Label>
                  Markedskjede
                  {props.changeRequest.medlemHosEkstraInfo?.markedsKjede && (
                    <Badge
                      bg={props.changeRequest.medlemTilOgMedDato ? 'danger' : 'success'}
                      className="ms-1"
                    >
                      {props.changeRequest.medlemTilOgMedDato ? 'Utmelding' : 'Innmelding'}
                    </Badge>
                  )}
                </Form.Label>
                <br />
                {props.changeRequest.medlemHosEkstraInfo?.markedsKjede ? (
                  <span>
                    <KjedePath
                      kjedeNavn={props.changeRequest.medlemHosEkstraInfo.markedsKjede.navn}
                      kjedeNr={parseInt(
                        props.changeRequest.medlemHosEkstraInfo.markedsKjede
                          .nummer as unknown as string,
                        10,
                      )}
                    />{' '}
                    <br />{' '}
                    {props.changeRequest.medlemFraOgMedDato
                      ? format(props.changeRequest.medlemFraOgMedDato, 'dd.MM.yyyy')
                      : ''}{' '}
                    {props.changeRequest.medlemTilOgMedDato
                      ? format(props.changeRequest.medlemTilOgMedDato, 'dd.MM.yyyy')
                      : ''}{' '}
                    {props.changeRequest.medlemsNummer
                      ? `medlemsnr. ${props.changeRequest.medlemsNummer}`
                      : ''}
                  </span>
                ) : (
                  <Form.Text className="text-muted">Ingen informasjon</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col xs="auto" md={2} className={Style.compareIconContainer}>
              <div className="mt-3">
                <CompareIcon
                  compareStatus={markedskjedeCompareStatus}
                  onClick={() => transferKjede(KjedeType.Marked)}
                />
              </div>
            </Col>
            <Col xs={12} md={5}>
              <div className="mb-3" style={{ position: 'relative' }}>
                <Form.Label>
                  Markedskjede
                  {props.activeMarkedskjede && (
                    <Badge bg="success" className="ms-2">
                      Aktiv
                    </Badge>
                  )}
                  {!props.activeMarkedskjede && props.futureMarkedsKjede && (
                    <Badge bg="primary" className="ms-2">
                      Fremtidig
                    </Badge>
                  )}
                </Form.Label>
                <br />

                {props.futureMarkedsKjede && (
                  <Form.Text className="text-muted">
                    <KjedePath
                      nameHierarchy={props.futureMarkedsKjede.nameHierarchy}
                      kjedeNrHierarchy={props.futureMarkedsKjede.kjedeNrHierarchy}
                    />{' '}
                    -{' '}
                    {props.futureMarkedsKjede.fraOgMedDato > new Date(1970, 1, 1)
                      ? format(props.futureMarkedsKjede.fraOgMedDato, 'dd.MM.yyyy')
                      : ''}
                  </Form.Text>
                )}

                {props.activeMarkedskjede && (
                  <div className="d-flex align-items-center gap-2">
                    <span>
                      <KjedePath
                        nameHierarchy={props.activeMarkedskjede.nameHierarchy}
                        kjedeNrHierarchy={props.activeMarkedskjede.kjedeNrHierarchy}
                      />{' '}
                      <br /> {format(props.activeMarkedskjede.fraOgMedDato, 'dd.MM.yyyy')}
                      {props.activeMarkedskjede.kundenrIKjede
                        ? ` avd. ${props.activeMarkedskjede.kundenrIKjede}`
                        : ''}
                    </span>
                  </div>
                )}

                {!props.activeMarkedskjede && !props.futureMarkedsKjede && (
                  <Form.Group>
                    <MarkedsKjedePicker
                      pickerMode={PickerMode.Single}
                      onSelectionChanged={handlePickedMarkedskjede}
                      selected={
                        props.selectedMarkedskjede?.kjedeNr
                          ? [props.selectedMarkedskjede.kjedeNr]
                          : []
                      }
                    />
                    {props.markedskjedeError && (
                      <Form.Text className="text-danger">{props.markedskjedeError}</Form.Text>
                    )}
                    {props.futureMarkedsKjede && (
                      <Form.Text className="text-warning">
                        Enheten har et fremtidig kjedemedlemsskap i{' '}
                        <KjedePath
                          nameHierarchy={props.futureMarkedsKjede.nameHierarchy}
                          kjedeNrHierarchy={props.futureMarkedsKjede.kjedeNrHierarchy}
                        />{' '}
                        fra og med{' '}
                        {props.futureMarkedsKjede.fraOgMedDato?.toLocaleDateString('no-NO')}
                      </Form.Text>
                    )}
                    <div className="row mt-3">
                      {viewData.showMarkedskjedeInnmeldingsFields && (
                        <>
                          <div className="col-md-6">
                            <Form.Label>Innmeldingsdato</Form.Label>
                            <NorwegianDatepicker
                              showFirstDaySuggestions={true}
                              minDate={props.minFraOgMedDato || undefined}
                              value={props.selectedMarkedskjede?.innmeldingsdato || null}
                              onChange={(d) => props.onChange('markedskjedeInnmeldingsdato', d)}
                            />
                          </div>
                          <div className="col-md-6">
                            <Form.Label>Medlemsnummer</Form.Label>
                            <Form.Control
                              size="sm"
                              value={props.selectedMarkedskjede?.medlemsnummer || ''}
                              onChange={(e) =>
                                props.onChange('markedskjedeMedlemsnummer', e.target.value)
                              }
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </Form.Group>
                )}
                {viewData.showMarkedskjedeUtmeldingsDato && (
                  <div className="col-md-6">
                    <Form.Label>Utmeldingsdato</Form.Label>
                    <NorwegianDatepicker
                      showLastDaySuggestions={true}
                      minDate={props.selectedMarkedskjede?.innmeldingsdato || undefined}
                      value={props.selectedMarkedskjede?.utmeldingsdato || null}
                      onChange={(d) => props.onChange('markedskjedeUtmeldingsdato', d)}
                    />
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Regionalkjede */}
          <Row className={Style.formGroupRow}>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <Form.Label>
                  Regionkjede
                  {props.changeRequest.medlemHosEkstraInfo?.regionalKjede && (
                    <Badge
                      bg={props.changeRequest.medlemTilOgMedDato ? 'danger' : 'success'}
                      className="ms-1"
                    >
                      {props.changeRequest.medlemTilOgMedDato ? 'Utmelding' : 'Innmelding'}
                    </Badge>
                  )}
                </Form.Label>
                <br />
                {props.changeRequest.medlemHosEkstraInfo?.regionalKjede ? (
                  <span>
                    <KjedePath
                      kjedeNavn={props.changeRequest.medlemHosEkstraInfo.regionalKjede.navn}
                      kjedeNr={parseInt(
                        props.changeRequest.medlemHosEkstraInfo.regionalKjede
                          .nummer as unknown as string,
                        10,
                      )}
                    />{' '}
                    <br />{' '}
                    {props.changeRequest.medlemFraOgMedDato
                      ? format(props.changeRequest.medlemFraOgMedDato, 'dd.MM.yyyy')
                      : ''}{' '}
                    {props.changeRequest.medlemTilOgMedDato
                      ? format(props.changeRequest.medlemTilOgMedDato, 'dd.MM.yyyy')
                      : ''}{' '}
                    {props.changeRequest.medlemsNummer
                      ? `medlemsnr. ${props.changeRequest.medlemsNummer}`
                      : ''}
                  </span>
                ) : (
                  <Form.Text className="text-muted">Ingen informasjon</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col xs="auto" md={2} className={Style.compareIconContainer}>
              <div className="mt-4">
                <CompareIcon
                  compareStatus={regionalkjedeCompareStatus}
                  onClick={() => transferKjede(KjedeType.Region)}
                />
              </div>
            </Col>
            <Col xs={12} md={5}>
              <div className="mb-3" style={{ position: 'relative' }}>
                <Form.Group>
                  <Form.Label>
                    Regionalkjede
                    {props.activeRegionalkjede && (
                      <Badge bg="success" className="ms-2">
                        Aktiv
                      </Badge>
                    )}
                    {!props.activeRegionalkjede && props.futureRegionalkjede && (
                      <Badge bg="primary" className="ms-2">
                        Fremtidig
                      </Badge>
                    )}
                  </Form.Label>
                  <br />

                  {props.futureRegionalkjede && (
                    <Form.Text>
                      <KjedePath
                        nameHierarchy={props.futureRegionalkjede.nameHierarchy}
                        kjedeNrHierarchy={props.futureRegionalkjede.kjedeNrHierarchy}
                      />{' '}
                      -{format(props.futureRegionalkjede.fraOgMedDato, 'dd.MM.yyyy')}
                    </Form.Text>
                  )}

                  {props.activeRegionalkjede && (
                    <div className="d-flex align-items-center gap-2">
                      <span>
                        <KjedePath
                          nameHierarchy={props.activeRegionalkjede.nameHierarchy}
                          kjedeNrHierarchy={props.activeRegionalkjede.kjedeNrHierarchy}
                        />{' '}
                        <br />
                        {props.activeRegionalkjede.fraOgMedDato > new Date(1970, 1, 1)
                          ? format(props.activeRegionalkjede.fraOgMedDato, 'dd.MM.yyyy')
                          : ''}
                        {props.activeRegionalkjede.kundenrIKjede
                          ? ` avd. ${props.activeRegionalkjede.kundenrIKjede}`
                          : ''}
                      </span>
                    </div>
                  )}
                </Form.Group>
                {!props.activeRegionalkjede && !props.futureRegionalkjede && (
                  <Form.Group>
                    <RegionalkjedePicker
                      pickerMode={PickerMode.Single}
                      onSelectionChanged={handlePickedRegionalkjede}
                      selected={
                        props.selectedRegionalkjede?.kjedeNr
                          ? [props.selectedRegionalkjede.kjedeNr]
                          : []
                      }
                    />
                    {props.regionalkjedeError && (
                      <Form.Text className="text-danger">{props.regionalkjedeError}</Form.Text>
                    )}
                    {props.futureRegionalkjede && (
                      <Form.Text className="text-warning">
                        Enheten har et fremtidig kjedemedlemsskap i{' '}
                        <KjedePath
                          nameHierarchy={props.futureRegionalkjede.nameHierarchy}
                          kjedeNrHierarchy={props.futureRegionalkjede.kjedeNrHierarchy}
                        />{' '}
                        fra og med{' '}
                        {props.futureRegionalkjede.fraOgMedDato?.toLocaleDateString('no-NO')}
                      </Form.Text>
                    )}
                    <div className="row mt-3">
                      {viewData.showRegionalInnmeldingsFields && (
                        <>
                          <div className="col-md-6">
                            <Form.Label>Innmeldingsdato</Form.Label>
                            <NorwegianDatepicker
                              showFirstDaySuggestions={true}
                              minDate={props.minFraOgMedDato || undefined}
                              value={props.selectedRegionalkjede?.innmeldingsdato || null}
                              onChange={(d) => props.onChange('regionalkjedeInnmeldingsdato', d)}
                            />
                          </div>
                          <div className="col-md-6">
                            <Form.Label>Medlemsnummer</Form.Label>
                            <Form.Control
                              size="sm"
                              value={props.selectedRegionalkjede?.medlemsnummer || ''}
                              onChange={(e) =>
                                props.onChange('regionalkjedeMedlemsnummer', e.target.value)
                              }
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </Form.Group>
                )}
                {viewData.showRegionalkjedeUtmeldingsDato && (
                  <div className="col-md-6">
                    <Form.Label>Utmeldingsdato</Form.Label>
                    <NorwegianDatepicker
                      showLastDaySuggestions={true}
                      value={props.selectedRegionalkjede?.utmeldingsdato || null}
                      onChange={(d) => props.onChange('regionalkjedeUtmeldingsdato', d)}
                      minDate={props.selectedRegionalkjede?.innmeldingsdato || undefined}
                    />
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Innkjøp */}
          <Row className={Style.formGroupRow}>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <Form.Label>
                  Innkjøpssamarbeid
                  {props.changeRequest.medlemHosEkstraInfo?.innkjopssamarbeid && (
                    <Badge
                      bg={props.changeRequest.medlemTilOgMedDato ? 'danger' : 'success'}
                      className="ms-1"
                    >
                      {props.changeRequest.medlemTilOgMedDato ? 'Utmelding' : 'Innmelding'}
                    </Badge>
                  )}
                </Form.Label>
                <br />
                {props.changeRequest.medlemHosEkstraInfo?.innkjopssamarbeid ? (
                  <span>
                    <EnhetsgrupperingPath
                      nr={props.changeRequest.medlemHosEkstraInfo.innkjopssamarbeid.nummer}
                    />{' '}
                    <br />{' '}
                    {props.changeRequest.medlemFraOgMedDato
                      ? format(props.changeRequest.medlemFraOgMedDato, 'dd.MM.yyyy')
                      : ''}{' '}
                    {props.changeRequest.medlemTilOgMedDato
                      ? format(props.changeRequest.medlemTilOgMedDato, 'dd.MM.yyyy')
                      : ''}{' '}
                    medlemsnr. {props.changeRequest.medlemsNummer}
                  </span>
                ) : (
                  <Form.Text className="text-muted">Ingen informasjon</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col xs="auto" md={2} className={Style.compareIconContainer}>
              <div className="mt-5">
                <CompareIcon compareStatus={innkjopCompareStatus} onClick={transferInnkjop} />
              </div>
            </Col>
            <Col xs={12} md={5}>
              <div className="mt-3" style={{ position: 'relative' }}>
                <Form.Group>
                  <Form.Label>
                    Innkjøpssamarbeid
                    {isActiveMemberOfSuggestedInnkjop && (
                      <Badge bg="success" className="ms-2">
                        Aktiv
                      </Badge>
                    )}
                  </Form.Label>
                  <br />

                  <Form.Group>
                    {props.aktiveEnhetsgrupperinger
                      .filter(
                        (o) =>
                          props.changeRequest.medlemHosEkstraInfo?.innkjopssamarbeid?.nummer ===
                          o.enhetsgrupperingsNr,
                      )
                      .map((innkjop) => (
                        <div key={innkjop.enhetsgrupperingsNr} className="mt-1">
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className={
                                props.changeRequest.medlemHosEkstraInfo?.innkjopssamarbeid
                                  ?.nummer === innkjop.enhetsgrupperingsNr
                                  ? ''
                                  : 'text-muted'
                              }
                            >
                              <EnhetsgrupperingPath nr={innkjop.enhetsgrupperingsNr} /> <br />
                              {format(innkjop.innmeldingsdato, 'dd.MM.yyyy')}
                              {innkjop.medlemsnr ? ` avd. ${innkjop.medlemsnr}` : ''}
                            </span>
                          </div>
                        </div>
                      ))}
                  </Form.Group>
                </Form.Group>

                {!isActiveMemberOfSuggestedInnkjop && (
                  <Form.Group className="mt-2">
                    <GrupperingPicker
                      key={props.selectedEnhetsgruppering?.enhetsgrupperingNr || 'no-selection'}
                      treeSelectMode="radioSelect"
                      enhetgrupperingType="IS"
                      onSelectionChanged={handlePickedGruppering}
                      findPreselectedNodesByNumber={() =>
                        props.selectedEnhetsgruppering?.enhetsgrupperingNr
                          ? [props.selectedEnhetsgruppering.enhetsgrupperingNr]
                          : []
                      }
                    />
                    {props.enhetsgrupperingError && (
                      <Form.Text className="text-danger">{props.enhetsgrupperingError}</Form.Text>
                    )}
                    {/* Show active membership info if selected enhetsgruppering is already a member */}
                    {isActiveMemberOfSelectedInnkjop && (
                      <>
                        {props.aktiveEnhetsgrupperinger
                          .filter(
                            (o) =>
                              o.enhetsgrupperingsNr?.toString() ===
                              props.selectedEnhetsgruppering?.enhetsgrupperingNr?.toString(),
                          )
                          .map((innkjop) => (
                            <div key={innkjop.enhetsgrupperingsNr} className="mt-2">
                              <div className="d-flex align-items-center gap-2">
                                <span>
                                  <EnhetsgrupperingPath nr={innkjop.enhetsgrupperingsNr} /> <br />
                                  {format(innkjop.innmeldingsdato, 'dd.MM.yyyy')}
                                  {innkjop.medlemsnr ? ` avd. ${innkjop.medlemsnr}` : ''}{' '}
                                  <Badge bg="success">Aktiv</Badge>
                                </span>
                              </div>
                            </div>
                          ))}
                        <div className="col-md-6 mt-3">
                          <Form.Label>Utmeldingsdato</Form.Label>
                          <NorwegianDatepicker
                            showLastDaySuggestions={true}
                            minDate={props.selectedEnhetsgruppering?.innmeldingsdato || undefined}
                            value={props.selectedEnhetsgruppering?.utmeldingsdato || null}
                            onChange={(d) => {
                              const nr = props.selectedEnhetsgruppering?.enhetsgrupperingNr;
                              if (d && nr) {
                                props.onInnkjopUtmelding({
                                  enhetsgrupperingNr: nr,
                                  utmeldingsdato: DateHelper.addNorwegianTimezone
                                    ? DateHelper.addNorwegianTimezone(d)
                                    : d,
                                });
                              } else {
                                props.onChange('enhetsgrupperingsUtmeldingsdato', null);
                                if (nr) props.onChange('enhetsgrupperingsNr', nr);
                              }
                            }}
                          />
                        </div>
                      </>
                    )}
                    {/* Show innmelding fields only when selected enhetsgruppering is NOT already a member */}
                    {viewData.showInnkjøpInnmeldingsFields && !isActiveMemberOfSelectedInnkjop && (
                      <div className="row mt-3">
                        <div className="col-md-6">
                          <Form.Label>Innmeldingsdato</Form.Label>
                          <NorwegianDatepicker
                            showFirstDaySuggestions={true}
                            value={props.selectedEnhetsgruppering?.innmeldingsdato || null}
                            onChange={(d) => props.onChange('enhetsgrupperingsInnmeldingsdato', d)}
                          />
                        </div>
                        <div className="col-md-6">
                          <Form.Label>Medlemsnummer</Form.Label>
                          <Form.Control
                            size="sm"
                            value={props.selectedEnhetsgruppering?.medlemsnummer || ''}
                            onChange={(e) =>
                              props.onChange('enhetsgrupperingsMedlemsnummer', e.target.value)
                            }
                          />
                        </div>
                      </div>
                    )}
                  </Form.Group>
                )}

                {viewData.showInnkjøpUtmeldingsDato && (
                  <div className="col-md-6">
                    <Form.Label>Utmeldingsdato</Form.Label>
                    <NorwegianDatepicker
                      showLastDaySuggestions={true}
                      minDate={props.selectedEnhetsgruppering?.innmeldingsdato || undefined}
                      value={props.selectedEnhetsgruppering?.utmeldingsdato || null}
                      onChange={settMeldUt}
                    />
                  </div>
                )}

                {otherActiveEnhetsgrupperinger.length > 0 && (
                  <Form.Label className="mt-3">
                    Andre aktive og fremtidige enhetsgrupperinger{' '}
                  </Form.Label>
                )}

                {showAllActiveEnhetsgrupper
                  ? otherActiveEnhetsgrupperinger.map((innkjop) => (
                      <div key={innkjop.enhetsgrupperingsNr} className="mt-1">
                        <div className="d-flex align-items-center gap-2">
                          <span>
                            <EnhetsgrupperingPath nr={innkjop.enhetsgrupperingsNr} /> -{' '}
                            {format(innkjop.innmeldingsdato, 'dd.MM.yyyy')}
                            {innkjop.medlemsnr ? ` avd. ${innkjop.medlemsnr}` : ''}{' '}
                            <Badge
                              bg={innkjop.innmeldingsdato > new Date() ? 'primary' : 'success'}
                            >
                              {innkjop.innmeldingsdato > new Date() ? 'Fremtidig' : 'Aktiv'}
                            </Badge>
                          </span>
                        </div>
                      </div>
                    ))
                  : otherActiveEnhetsgrupperinger.length > 0 && (
                      <div className="mt-1">
                        <div className="d-flex align-items-center gap-2">
                          <span>
                            {otherActiveEnhetsgrupperinger[0].toppnivaaEnhetsgrupperingnavn &&
                            otherActiveEnhetsgrupperinger[0].toppnivaaEnhetsgrupperingnavn !==
                              otherActiveEnhetsgrupperinger[0].enhetsgruppering
                              ? `${otherActiveEnhetsgrupperinger[0].toppnivaaEnhetsgrupperingnavn} / ${otherActiveEnhetsgrupperinger[0].enhetsgruppering}`
                              : otherActiveEnhetsgrupperinger[0].enhetsgruppering}{' '}
                            ({otherActiveEnhetsgrupperinger[0].enhetsgrupperingsNr}) -{' '}
                            {format(otherActiveEnhetsgrupperinger[0].innmeldingsdato, 'dd.MM.yyyy')}
                            {otherActiveEnhetsgrupperinger[0].medlemsnr
                              ? ` avd. ${otherActiveEnhetsgrupperinger[0].medlemsnr}`
                              : ''}{' '}
                            <Badge
                              bg={
                                otherActiveEnhetsgrupperinger[0].innmeldingsdato > new Date()
                                  ? 'primary'
                                  : 'success'
                              }
                            >
                              {otherActiveEnhetsgrupperinger[0].innmeldingsdato > new Date()
                                ? 'Fremtidig'
                                : 'Aktiv'}
                            </Badge>
                            {otherActiveEnhetsgrupperinger.length > 1 && (
                              <Badge
                                bg="primary"
                                pill
                                className="ms-1"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setShowAllActiveEnhetsgrupper(true)}
                              >
                                +{otherActiveEnhetsgrupperinger.length - 1}
                              </Badge>
                            )}
                          </span>
                        </div>
                      </div>
                    )}

                {showAllActiveEnhetsgrupper && otherActiveEnhetsgrupperinger.length > 1 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 mt-1"
                    onClick={() => setShowAllActiveEnhetsgrupper(false)}
                  >
                    Vis færre
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </Collapse>
    </>
  );
};

export default MedlemhosRow;
